import type { Table } from "common/data/table";

/**
 * @param entities - Array of entities that have a sortIndex property.
 *
 * Will ensure that there are no duplicate sort indices for a given set of entities
 * and that a value is set if not already defined
 */
export function sanitizeSortIndices<T extends { sortIndex: number }>(entities: T[]): T[] {
  return entities.map((x, sortIndex) => ({ ...x, sortIndex }));
}

/**
 * @param sortIndices - Array of sortIndex values.
 *
 * Will determine whether a series of sortIndex is in a valid sequence and/or
 * whether or not each value is a number.
 */
export function validateSortIndices(sortIndices: number[]) {
  return sortIndices.every((sortIndex, i) => !Number.isNaN(sortIndex) && (i === 0 || sortIndices[i - 1] < sortIndex));
}

export function sortEntitiesBySortIndex<T extends { sortIndex: number }>(entities: T[], descending?: boolean): T[] {
  if (!descending) {
    return entities.sort((a, b) => (a.sortIndex > b.sortIndex ? 1 : -1));
  } else {
    return entities.sort((a, b) => (a.sortIndex < b.sortIndex ? 1 : -1));
  }
}

export function sortTableBySortIndex<T extends { sortIndex: number }>(entities: Table<T>): T[] {
  return sortEntitiesBySortIndex(Object.values(entities));
}

export function maxSortIndex(items: { sortIndex?: number }[]) {
  return items.reduce((a, b) => Math.max(a, b.sortIndex ?? a), 0);
}

export function maxSortIndexFromTable(items: Table<{ sortIndex: number }>) {
  return maxSortIndex(Object.values(items));
}

export function nextSortIndexFromTable(items: Table<{ sortIndex: number }>) {
  const tableItems = Object.values(items);

  if (!tableItems.length) {
    return 0;
  }

  return maxSortIndex(tableItems) + 1;
}
